import * as THREE from 'three'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useThree } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'

import { const_var, params } from '../../../../redux/reducers/reducer'
import { setLeanToLegPosObj, setMainTrussPosInfo, setTrussTypeName } from '../../../../action'
import { TrussRailTextureImg } from '../../../../utils/Textures'
import { delModel, extrudeSettings, textureAnisotropy } from '../../../../utils/Functions'
import { CSG } from '../../../../assets/libs/csg-lib'

import ACDT from './ACDT'
import ACT from './ACT'
import CTCT from './CTCT'
import ECT from './ECT'
import LST_RNT from './LST_RNT'
import QCT from './QCT'
import SBST from './SBST'
import TWCT from './TWCT'
import AST from './AST'
import LCT from './LCT'
import CMT from './CMT'
import Standard from './Standard'

const Truss = ({ overlapTrussArr, isFor='center' }) => {
    const dispatch = useDispatch();
    
    const { gl } = useThree();
    const [hiddenBaseRailIdArr, setHiddenBaseRailIdArr] = useState([]);
    const nMap = useTexture( process.env.REACT_APP_BASE_URL+TrussRailTextureImg);

    // const tempTrussType = ['ACDT', 'ACT', 'AST', 'CMT', 'CTCT', 'ECT', 'LCT', 'LST', 'QCT', 'SBST', 'TWCT'];
    // const trussType = ''

    const trussType = useMemo(() => {
        let trussObj = const_var.a_p_d_a?.truss_name
        let trussName = ""
        if(trussObj != undefined && trussObj.length > 0){
            trussObj.forEach((item)=>{
                if(item.is_show == 1 && (params.trussType == item.name.replace(/[0-9]/g, ''))){
                    trussName = item.name.replace(/[0-9]/g, '')
                }else{
                    trussName = (params.p_b_t === 27) ? 'Standard' : (params.p_w > 30) ? (!params.singleSlope ? params.trussType : "") : ((params.p_w > 24 && params.p_w <= 30) && !params.singleSlope) ? "TWCT" : ""
                }
            })
        }
        
        // return (params.p_b_t === 27) ? 'Standard' : (params.p_w > 30) ? (!params.singleSlope ? params.trussType : "") : ((params.p_w > 24 && params.p_w <= 30) && !params.singleSlope) ? "TWCT" : ""
        // return (const_var.a_p_d_a?.truss_name?.length > 1 ) ? params["trussType"] : (params.p_b_t === 27) ? 'Standard' : (params.p_w > 30) ? (!params.singleSlope ? params.trussType : "") : ((params.p_w > 24 && params.p_w <= 30) && !params.singleSlope) ? "TWCT" : ""
        return trussName
    }, [params.p_b_t,const_var.a_p_d_a?.truss_name, params.p_w, params.trussType, params.singleSlope,const_var.a_p_d_a?.truss_name?.length]);
    
    const trussTexture = useMemo(() => {
        let envMap = const_var.envIMG
        textureAnisotropy(gl, envMap, 1, 1, 0)
        return envMap
    }, []);
    
    const norMap = useMemo(() => {
        let normalMap = nMap
        textureAnisotropy(gl, normalMap, 1, 1, Math.PI / 2)
        return normalMap
    }, []);

    const legDistance = useMemo(() => {
        return (const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined) ? const_var.DistanceArr[params.p_b_t][params.p_r_s] : 5
    }, [const_var.DistanceArr, params.p_b_t, params.p_r_s,params.fourth_center_cost,const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined && const_var.DistanceArr[params.p_b_t][params.p_r_s]])
    
    const trussModelPosInfoArr = useMemo(() => {
        let count, distance = 0;
        
        const initDistance = legDistance;
        const tempCount = const_var.isUpdate4ftLegLogic ? Math.ceil(params.p_d / initDistance) : Math.floor(params.p_d / initDistance);
        if (params.p_d === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if (params.p_d - tempCount * initDistance < initDistance / 2) {
            count = tempCount;
            distance = params.p_d / count;
        } else {
            count = tempCount + 1;
            distance = params.p_d / count;
        }
        
        const posInfoArr = Array.from({ length: count + 1 }, (_, index) => distance * index - params.p_d / 2 + (index === 0 && 0.05) + (index === count && -0.05))

        if (posInfoArr.length > 0) dispatch(setMainTrussPosInfo(posInfoArr))
                    
        // push the valuse of porches leg position to highlight 
        let sortedPosArr = JSON.parse(JSON.stringify(posInfoArr))
        sortedPosArr.sort(function(a, b){return a - b});
        dispatch(setLeanToLegPosObj(sortedPosArr, 'center'));

        return posInfoArr
    }, [params.p_d, legDistance, params.trussType])

    let meshDelModel_CSG = {};
    const wallValueArr = ['front', 'back', 'left', 'right', 'backStorageFront', 'leftStorageFront', 'rightStorageFront'];
    wallValueArr.map((item) => {
        const meshDelModel = new THREE.Mesh(
            new THREE.ExtrudeGeometry(delModel(item, 'leg'), extrudeSettings(3)),
            new THREE.MeshStandardMaterial({ color: 'red' })
        )
        const meshDelModel_Temp = meshDelModel.clone();
        if (item === 'front' || item === 'back'|| item === 'backStorageFront') {
            meshDelModel_Temp.position.set(0, 0, -1.5)
        } else {
            meshDelModel_Temp.rotateY(-Math.PI / 2)
            meshDelModel_Temp.position.set(1.5, 0, 0)
        }
        meshDelModel_Temp.updateMatrix();
        meshDelModel_Temp.updateMatrixWorld();

        const mesh_CSG = CSG.fromMesh(meshDelModel_Temp);

        meshDelModel_CSG = { ...meshDelModel_CSG,  [item]: mesh_CSG };

        return null;
    })

    useEffect(() => {
        if(const_var.c_m_a[params.p_b_t] != undefined && const_var.c_m_a[params.p_b_t].trussType!=undefined && const_var.c_m_a[params.p_b_t].trussType.length > 0){
            let checkTruss =  const_var.c_m_a[params.p_b_t].trussType.filter(data=> params.p_w <= data.max_width && params.p_w >= data.min_width);
            if(checkTruss.length == 0){
                params["trussType"] = 'ECT'
            }
        }
    }, [params.toggleBuildingStyleChange, params.p_b_t, params.newtrussType, params.trussType,params.p_w,const_var.c_m_a[params.p_b_t]?.trussType?.[0]?.name]);
    
    useEffect(() => {
        if(const_var.c_m_a[params.p_b_t] != undefined && (params.newtrussType == undefined || params.newtrussType=='')){
            if(const_var.c_m_a[params.p_b_t].trussType!=undefined && const_var.c_m_a[params.p_b_t].trussType.length > 0){
                if(const_var.c_m_a[params.p_b_t].trussType.length == 1){
                    // params["trussType"] = const_var.c_m_a[params.p_b_t].trussType[0].name.replace(/[0-9]/g, '')
                    let temp = const_var.c_m_a[params.p_b_t].trussType[0].name.replace(/[0-9]/g, '')
                    dispatch(setTrussTypeName(temp))
                }else{
                    let filterDatan = const_var.c_m_a[params.p_b_t].trussType.filter(data=>data.name.includes(params.trussType));
                    let defaultTruss  = const_var.c_m_a[params.p_b_t]?.trussType?.find((data) => data.is_show == 0)
                    // params["trussType"] = (filterDatan?.length>0 && const_var.crmSetting.is_Edit == true)?filterDatan[0].name.replace(/[0-9]/g, ''): defaultTruss?.name?.replace(/[0-9]/g, '')
                    let temp = (filterDatan?.length>0 && const_var.crmSetting.is_Edit == true)?filterDatan[0].name.replace(/[0-9]/g, ''): defaultTruss?.name?.replace(/[0-9]/g, '')
                    dispatch(setTrussTypeName(temp))
                }
            }
        }
    }, [params.toggleBuildingStyleChange, params.p_b_t, params.newtrussType, params.trussType,const_var.c_m_a[params.p_b_t]?.trussType?.[0]?.name])


    useMemo(() => {
        let isHiddenBaseRailIndexArr = [];
        const heightestDoorArr = const_var.newDoorsArray.filter(e => (e.height > params.p_h) || (e.positionY + e.height / 2 >= params.p_h));
        
        if(heightestDoorArr.find(item => item.userData.wallName === 'c_b_f_w')) isHiddenBaseRailIndexArr.push(trussModelPosInfoArr?.length - 1)
        if(heightestDoorArr.find(item => item.userData.wallName === 'c_b_b_w')) isHiddenBaseRailIndexArr.push(0)
        if(params.p_d % legDistance === 0){
            if(heightestDoorArr.find(item => item.userData.wallName === 'c_b_f_s_w')) isHiddenBaseRailIndexArr.push(parseInt(params.p_u_t) / legDistance)
        }
        
        setHiddenBaseRailIdArr(isHiddenBaseRailIndexArr);
    }, [params.p_h, const_var.newDoorsArray, params.p_u_t, params.p_d, params.fourth_center_cost, legDistance])

    switch (trussType) {
        case "RBCAT":
        case "RBCBT":
        case "TWCT":
            return <TWCT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "ACDT":
            return <ACDT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "ACT":
            return <ACT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "HSST":
        case "AST":
            return <AST trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "CTCT":
            return <CTCT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "ECT":
            return <ECT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "RBT":
        case "LCT":
            return <LCT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "LST": 
        case "RNT":
            return <LST_RNT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "QCT":
            return <QCT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "SBST":
            return <SBST trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        case "CMT":
            return <CMT trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
        default:
            return <Standard trussType={trussType} normalMap={norMap} envMap={trussTexture} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} meshDelModel_CSG={meshDelModel_CSG} isFor={isFor} />
    }
}

export default Truss
